import { useFloating, flip, shift, offset } from '@floating-ui/react-dom';
import { Add } from '@mui/icons-material';
import { Button, Checkbox, IconButton, Tab, Tabs, Typography, Slider } from '@mui/material';
import { Box } from '@mui/system';
import { colorFromHSV, DEFAULT_COLOR, fromHexColor } from 'common/ColorUtils';
import { FlexColumn } from 'common/FlexColumn';
import { FlexRow } from 'common/FlexRow';
import { HSVtoRGB, RGBtoHSV } from 'common/HSVConverter';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { SketchPicker } from 'react-color';
import { ColorPresetStore } from 'stores/editor/ColorPresetStore';

type ColorPresetPanelProps = {
  store: ColorPresetStore;
  onCalculateDefaultBaseColor?: () => void;
};

export const ColorPresetPanel = observer((props: ColorPresetPanelProps) => {
  const { store } = props;

  const colorPresetSelected = store.currentPresetIndex !== undefined && store.currentPresetIndex !== null;
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [isBaseColorPicker, setIsBaseColorPicker] = React.useState(false);
  
  // Floating UI for smart positioning
  const { refs, floatingStyles } = useFloating({
    open: pickerOpen,
    placement: 'top-start',
    middleware: [
      offset(8), // 8px spacing from anchor
      flip({ 
        fallbackPlacements: ['top-end', 'bottom-start', 'bottom-end'] // Prefer top placements first
      }),
      shift({ padding: 8 }) // Stay within viewport bounds
    ]
  });

  // Common slider styling
  const commonSliderSx = {
    padding: '0px',
    height: 8,
    '& .MuiSlider-track': {
      background: 'transparent',
      border: 'none'
    },
    '& .MuiSlider-thumb': {
      width: 16,
      height: 16,
      backgroundColor: '#fff',
      border: '2px solid currentColor'
    }
  };

  // Convert current HSV values to RGB for the color picker
  const getCurrentColor = () => {
    if (isBaseColorPicker) {
      // Convert base color hex to RGB
      const baseColorRgb = fromHexColor(store.baseColor);
      return { r: baseColorRgb.r, g: baseColorRgb.g, b: baseColorRgb.b };
    }
    
    if (!colorPresetSelected) {
      return { r: 255, g: 255, b: 255 };
    }
    
    const preset = store.presets[store.currentPresetIndex];
    const hue = preset.hueTarget / 360; // Convert degrees to 0-1 range
    const saturation = preset.saturationMultiplier / 2.0; // Convert 0-2 range to 0-1
    const value = preset.valueMultiplier; // Already in 0-1 range
    
    return HSVtoRGB(hue, saturation, value);
  };

  // Convert current color to HSL for sliders
  const getCurrentHSLColor = () => {
    const rgb = getCurrentColor();
    // Convert RGB to HSL 
    const { r, g, b } = rgb;
    const max = Math.max(r, g, b) / 255;
    const min = Math.min(r, g, b) / 255;
    const diff = max - min;
    const sum = max + min;
    const l = sum / 2;

    let h = 0;
    let s = 0;

    if (diff !== 0) {
      s = l > 0.5 ? diff / (2 - sum) : diff / sum;
      
      switch (max) {
        case r / 255:
          h = ((g / 255 - b / 255) / diff + (g < b ? 6 : 0)) / 6;
          break;
        case g / 255:
          h = ((b / 255 - r / 255) / diff + 2) / 6;
          break;
        case b / 255:
          h = ((r / 255 - g / 255) / diff + 4) / 6;
          break;
      }
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
      a: 1
    };
  };

  // Handle color change from picker
  const handleColorChange = (color: any) => {
    if (isBaseColorPicker) {
      // Handle base color change
      const { r, g, b } = color.rgb;
      const hexColor = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}FF`;
      store.setBaseColor(hexColor);
      return;
    }
    
    if (!colorPresetSelected) return;
    
    const { r, g, b } = color.rgb;
    const hsv = RGBtoHSV(r, g, b);
    
    // Convert back to the store's expected ranges
    const hueTarget = hsv.h * 360; // Convert 0-1 to degrees
    const saturationMultiplier = hsv.s * 2.0; // Convert 0-1 to 0-2 range
    const valueMultiplier = hsv.v; // Keep 0-1 range
    
    // Use the existing public methods to update values
    store.rotateHueSlider(null, hueTarget);
    store.rotateSaturationSlider(null, saturationMultiplier);
    store.rotateValueSlider(null, valueMultiplier);
  };

  // Handle color change from sliders
  const handleSliderChange = (newColor: any) => {
    // Convert HSL back to RGB and then to HSV for the store
    const { h, s, l } = newColor;
    
    // Convert HSL to RGB
    const hue = h / 360;
    const saturation = s / 100;
    const lightness = l / 100;
    
    const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
    const x = c * (1 - Math.abs((hue * 6) % 2 - 1));
    const m = lightness - c / 2;
    
    let r = 0, g = 0, b = 0;
    
    if (hue < 1/6) {
      r = c; g = x; b = 0;
    } else if (hue < 2/6) {
      r = x; g = c; b = 0;
    } else if (hue < 3/6) {
      r = 0; g = c; b = x;
    } else if (hue < 4/6) {
      r = 0; g = x; b = c;
    } else if (hue < 5/6) {
      r = x; g = 0; b = c;
    } else {
      r = c; g = 0; b = x;
    }
    
    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);
    
    // Now use the same logic as handleColorChange
    if (isBaseColorPicker) {
      const hexColor = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}FF`;
      store.setBaseColor(hexColor);
      return;
    }
    
    if (!colorPresetSelected) return;
    
    const hsv = RGBtoHSV(r, g, b);
    const hueTarget = hsv.h * 360;
    const saturationMultiplier = hsv.s * 2.0;
    const valueMultiplier = hsv.v;
    
    store.rotateHueSlider(null, hueTarget);
    store.rotateSaturationSlider(null, saturationMultiplier);
    store.rotateValueSlider(null, valueMultiplier);
  };

  // Handle tab click to open color picker
  const handleTabClick = (event: React.MouseEvent, index: number) => {
    // Prevent the click from being treated as an "outside click"
    event.stopPropagation();
    
    // Set the reference element for Floating UI positioning
    refs.setReference(event.currentTarget as HTMLElement);
    setIsBaseColorPicker(false);
    setPickerOpen(true);
  };

  // Handle base color button click to open color picker
  const handleBaseColorClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    
    // Set the reference element for Floating UI positioning
    refs.setReference(event.currentTarget as HTMLElement);
    setIsBaseColorPicker(true);
    setPickerOpen(true);
  };

  // Handle default color button click
  const handleDefaultColorClick = () => {
    if (props.onCalculateDefaultBaseColor) {
      props.onCalculateDefaultBaseColor();
    } else {
      // Fallback to setting the DEFAULT_COLOR constant
      store.setBaseColor(DEFAULT_COLOR);
    }
  };

  // Handle click outside to close popup
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Don't close if clicking on the popup itself
      if (refs.floating.current && refs.floating.current.contains(target)) {
        return;
      }
      
      // Don't close if clicking on the reference element (button/tab)
      if (refs.reference.current && 'contains' in refs.reference.current && refs.reference.current.contains(target)) {
        return;
      }
      
      setPickerOpen(false);
    };

    if (pickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [pickerOpen, refs.floating, refs.reference]);

  return (
    <>
      <FlexRow justifyContent="flex-start" alignItems="flex-start">
        <FlexColumn alignItems="flex-start">
          <FlexRow alignItems="center" gap={1}>
            Base Color:
            <Button
              size="small"
              onClick={handleDefaultColorClick}
            >
              (Mean image color)
            </Button>
          </FlexRow>

          <Button
            onClick={handleBaseColorClick}
            sx={{ padding: 0, minWidth: 'auto' }}
          >
            <Box 
              bgcolor={store.baseColor} 
              width='80px' 
              height='40px'
              sx={{
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            ></Box>
          </Button>
        </FlexColumn>
      </FlexRow>
        <FlexRow>
        Use Base as Preset?
        <Checkbox checked={store.useBaseAsPreset} onChange={() => store.toggleBasePreset()} />
        <Button
          color='secondary'
          onClick={store.removePreset}
          disabled={!colorPresetSelected}
        >
          Delete Preset
        </Button>
      </FlexRow>
      
      <FlexRow flexGrow={1}>
        <Tabs
          variant='scrollable'
          value={store.currentPresetIndex ?? false}
          onChange={(ev, value) => {
            store.selectPreset(value);
          }}
          sx={{ borderRight: 1, borderColor: 'divider' }}
          scrollButtons='auto'
        >
          {store.presets.map((v, index) => {
            return (
              <Tab
                value={index}
                label={`${index + 1}`}
                key={index}
                onClick={(event) => handleTabClick(event, index)}
                sx={{
                  minWidth: '80px',
                  minHeight: '40px',
                  backgroundColor: colorFromHSV(
                    v.hueTarget / 360,
                    v.saturationMultiplier / 2.0,
                    v.valueMultiplier,
                  ),
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  margin: '2px',
                }}
              />
            );
          })}
        </Tabs>
        <IconButton onClick={() => store.addPreset()}>
          <Add />
        </IconButton>
      </FlexRow>
      
      {/* Floating Color Picker Popup */}
      {pickerOpen && (isBaseColorPicker || colorPresetSelected) && (
        <Box
          ref={refs.setFloating}
          style={floatingStyles}
          sx={{
            zIndex: 1000,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            width: '280px',
            maxHeight: '70vh',
          }}
        >
          {/* Color Picker on Top */}
          <Box sx={{ 
            mb: 1, 
            display: 'flex', 
            justifyContent: 'center',
            '& .sketch-picker': {
              boxShadow: 'none !important',
              padding: '8px !important',
            },
            '& .sketch-picker > div:nth-of-type(2)': {
              display: 'none !important' // Hide hue slider
            },
            '& .sketch-picker > div:nth-of-type(3)': {
              paddingTop: '8px !important' // Reduce spacing above inputs
            }
          }}>
            <SketchPicker
              color={getCurrentColor()}
              onChange={handleColorChange}
              disableAlpha={true}
              presetColors={[]} // Remove preset colors
              width="240px"
            />
          </Box>
          
          {/* HSL Sliders on Bottom */}
          <Box sx={{ 
            '& > *': { 
              mb: 0,
              width: '100%',
              padding: '0px'
            } 
          }}>
            {/* Hue Slider */}
            <Box>
              <Slider
                value={getCurrentHSLColor().h}
                onChange={(_, value) => handleSliderChange({ 
                  ...getCurrentHSLColor(), 
                  h: value as number 
                })}
                min={0}
                max={360}
                size="small"
                sx={{
                  ...commonSliderSx,
                  '& .MuiSlider-rail': {
                    background: 'linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)',
                    height: 8,
                    opacity: 1
                  }
                }}
              />
            </Box>

            {/* Saturation Slider */}
            <Box>
              <Slider
                value={getCurrentHSLColor().s}
                onChange={(_, value) => handleSliderChange({ 
                  ...getCurrentHSLColor(), 
                  s: value as number 
                })}
                min={0}
                max={100}
                size="small"
                sx={{
                  ...commonSliderSx,
                  '& .MuiSlider-rail': {
                    background: `linear-gradient(to right, hsl(${getCurrentHSLColor().h}, 0%, ${getCurrentHSLColor().l}%), hsl(${getCurrentHSLColor().h}, 100%, ${getCurrentHSLColor().l}%))`,
                    height: 8,
                    opacity: 1
                  }
                }}
              />
            </Box>

            {/* Lightness Slider */}
            <Box>
              <Slider
                value={getCurrentHSLColor().l}
                onChange={(_, value) => handleSliderChange({ 
                  ...getCurrentHSLColor(), 
                  l: value as number 
                })}
                min={0}
                max={100}
                size="small"
                sx={{
                  ...commonSliderSx,
                  '& .MuiSlider-rail': {
                    background: `linear-gradient(to right, #000000, hsl(${getCurrentHSLColor().h}, ${getCurrentHSLColor().s}%, 50%), #ffffff)`,
                    height: 8,
                    opacity: 1
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
});
